import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import type { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import type { Grid } from '~/engine/data/common/Grid';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * Tells the user, once per structure set, which of its point ROIs lie
 * outside the image it is shown on. The points stay where they were saved.
 */
export function usePointsOutsideImageNotice(
    structureSet: StructureSetReadModel | undefined,
    imageGrid: Grid | undefined
): void {
    const intl = useIntl();
    const toldRef = useRef(new Set<string>());
    useEffect(() => {
        if (
            !structureSet ||
            !imageGrid ||
            toldRef.current.has(structureSet.dicomUid)
        ) {
            return;
        }
        toldRef.current.add(structureSet.dicomUid);
        const outside = structureSet.roiArray.filter(
            ({ isPointRoi, pointCoordinatesMm }) =>
                isPointRoi &&
                pointCoordinatesMm &&
                !imageGrid.includesPoint(pointCoordinatesMm)
        );
        if (outside.length === 0) {
            return;
        }
        const names = intl.formatList(
            outside.map(({ name }) => name),
            { type: 'conjunction' }
        );
        enqueueSnackbar(
            intl.formatMessage(
                { id: 'poi.loaded.outsideImage' },
                { count: outside.length, names }
            ),
            {
                variant: 'warning',
                // Structure names stay out of the console.
                logMessage: `${outside.length} point ROI(s) lie outside the image`,
            }
        );
    }, [structureSet, imageGrid, intl]);
}
